import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { FaChevronLeft } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Grid, useTheme } from '@mui/material'

import { useGET_assignmentByIdQuery } from 'redux/api/assignmentsApi'
import { useGET_assignmentAssessQuery, usePOST_assignmentAssessMutation } from 'redux/api/assignmentsApi'
import { useGET_studentsProfileSelectQuery, useGET_studentsQuery } from 'redux/api/studentsApi'
import { useGET_submissionsQuery } from 'redux/api/submissionsApi'

import { Form, Formik } from 'formik'

import SubmitButton from 'components/atoms/buttons/SubmitButton'
import AssessRow from 'components/utilized/assignment/AssessRow'

import { useErrorAlert } from 'utils/customHooks/useErrorAlert'
import { useSuccessAlert } from 'utils/customHooks/useSuccessAlert'
import { THEME_GRAY } from 'utils/customizedTheme'
import formModel, { initialValues, validationSchema } from 'utils/formModels/assignmentAssessModel'
import { crudColorMap, crudIconMap } from 'utils/helpers'

import { ActionType, IAssessData, IColumn } from 'types'

interface IProps {
  action: ActionType
  assignmentId: string
  viewOnly: boolean
  tabText?: string
  tabKey?: string
  userRole?:string
}

const AssessForm: FC<IProps> = ({ action, assignmentId, viewOnly }) => {
  const navigate = useNavigate()

  const backToSlug = '/assignments'
  const { t } = useTranslation()
  const theme = useTheme()

  const { data } = useGET_assignmentAssessQuery(assignmentId, {
    skip: !assignmentId,
  })
  
  const { data: assignmentData } = useGET_assignmentByIdQuery(assignmentId, {
    skip: !assignmentId,
  })

  const { data: submissionsData } = useGET_submissionsQuery({
    assignment: assignmentId,
    ordering: '-id'
  })

  const { data: studentsData } = useGET_studentsQuery({group_course: assignmentData?.group_course?.id}, {
    skip: !assignmentData || !assignmentData.group_course,
  })


  /*
  const serverValues = {
    ...data,
    students: data?.students.map(student => {
      return {
        ...student,
        submissions: submissionsData?.results.filter(s => student.student.title.indexOf(s.student) > -1) ?? []
      }
    }) ?? []
  }
  */


  const studentsList = assignmentData?.recipient_type == 'everyone' 
    ?  studentsData?.results.map(s => {
      return {id: s.student_profile_id, title: s.full_name}
    }) ?? [] 
    : assignmentData?.students ?? []



  const serverValues = {
    ...data,
    students: studentsList.map(student => {

      const dataStudent = data?.students.find(s => s.student_id === student.id)

      return {
        student: {
          id: student.id,
          title: student.title
        },
        student_id: student.id,
        comment: '',
        observation: '',
        observed_at: null,
        final_grade_points: '',
        final_grade_percentage: '',
        reported_time: '',
        grading_components: assignmentData.grading_components.map(c => {return {
          grading_component: {
            id: c.id,
            title: c.title,
          },
          grading_component_id: c.id,
          grade_points: '',
          grade_percentage: ''
        }}),
        submissions: submissionsData?.results.filter(s => student.title.indexOf(s.student) > -1) ?? [],

        ...dataStudent
      }

    }) ?? []
  }

  const columns: IColumn[] = [
    {
      title: `${t('common.student')}`,
      width: '25%',
    },
    {
      title: `${t('common.objective-grades')}`,
      width: '20%',
    },
    {
      title: `${t('assignments.final-grade')}`,
      width: '15%',
    },
    {
      title: `${t('common.submissions')}`,
      width: '40%',
    },
  ]


  return (
    data && assignmentData && submissionsData &&
    <Formik
      enableReinitialize
      initialValues={serverValues}
      validationSchema={validationSchema}
      onSubmit={(values: any) => {
      }}
    >
      {({ values, resetForm }) => {
        return (
          <Form style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>

            <Grid container sx={{ marginBottom: '40px' }}>
              {assignmentData?.course_component &&
              <Grid item xs={12} md={3}>
                <div className="details-row">
                  <span className="details-label">{t('common.category')}:</span> 
                  <span className="details-content">{assignmentData?.course_component?.title}</span>
                </div>
              </Grid>
               }
              <Grid item xs={12} md={3}>
                <div className="details-row">
                  <span className="details-label">{t('common.submission-type')}:</span> 
                  <span className="details-content">{t(`submission-type.${assignmentData?.submission_type?.id}`)}</span>
                </div>
              </Grid>
              <Grid item xs={12} md={3}>
               <div className="details-row">
                  <span className="details-label">{t('common.assessment-type')}:</span> 
                  <span className="details-content">{t(`common.${assignmentData?.assessment_type?.id}`)}</span>
                </div>
              </Grid>
              {assignmentData?.rubric &&
              <Grid item xs={12} md={3}>
                <div className="details-row">
                  <span className="details-label">{t('common.rubric')}:</span> 
                  <span className="details-content">{assignmentData?.rubric?.title}</span>
                </div>
              </Grid>
               }
           </Grid>
 
            <Grid
              container
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <div className='table'>
                <div className='table-head'>
                  <div
                    className='table-row'
                    style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
                  >
                    {columns.map((column: any, i: number) => (
                      <div
                        className='table-cell'
                        key={i}
                        style={{
                          minWidth: column.minWidth ? column.minWidth : 'auto',
                          fontWeight: '700',
                          color: THEME_GRAY,
                          width: column.width,
                        }}
                      >
                        <>
                          <div style={{ display: 'flex', flexWrap: 'nowrap' }}>{column.title}</div>
                        </>
                      </div>
                    ))}
                  </div>
                </div>
                <div className='table-body'>

                  {serverValues && serverValues?.students.map((student) => {
                      return (
                        <div
                          className='table-row'
                          key={student.student_id}
                          style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
                        > 
                          <AssessRow  student={student} assignmentData={assignmentData} />
                        </div>
                      )
                    })}
                  

                </div>
              </div>

              <Grid container spacing={4} sx={{ marginTop: '1rem' }}>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex' }} className="transparent-background">
                    <Button
                      variant='outlined'
                      color='black'
                      onClick={() => navigate(backToSlug)}
                      sx={{ minHeight: '2.5rem', width: '10rem' }}
                    >
                      <FaChevronLeft style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                      {`${t('common.back')}`}
                    </Button>

                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Form>
        )
      }}
    </Formik>
  )
}

export default AssessForm
